import { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { isOrderAttrFilterActive } from 'src/components/dispatch/orderAttrFilter';
import { orderAttrFilterToken } from 'src/components/dispatch/orderAttrFilterParams';
import { useDebouncedOrderAttrFilter } from 'src/components/dispatch/useDebouncedOrderAttrFilter';
import { useOrderAttrFilter } from 'src/components/dispatch/useOrderAttrFilter';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  useConvenienceDispatchAllDeliveriesQuery,
  useConvenienceDispatchDeliveriesQuery,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import {
  useConvenienceDispatchFixedRouteDetailQuery,
  useConvenienceDispatchFixedRoutesQuery,
} from 'src/hooks/useConvenienceDispatchFixedRoutes.query';
import { useApplyFixedRouteMutation } from 'src/hooks/useConvenienceDispatchMutation';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { findLegRunIdsToSupplement, useQuerySupplementalLegRuns } from 'src/hooks/useQuerySupplementalLegRuns';
import { useSelectedFixedRouteId } from 'src/hooks/useSelectedFixedRouteId';
import { DateModel } from 'src/models/DateModel';
import { normalizePlanningEndOn } from 'src/utils/planningDateRange.util';

import ConvenienceDispatchesPresenter from './Presenter';

// 全ページを並列で取り切るので、この値はレスポンスの分割幅ではなく「同時に飛ぶ本数」を決める。
// 62 台規模で 4 ページ。小さくするとブラウザの同一オリジン 6 並列に当たってキューが伸び、
// 大きくすると 1 本のサーバ処理が重くなる（log_web_issues#301 の計測）
const PER_PAGE_DELIVERIES = 20;
const PER_PAGE_LEG_RUNS = 200;

const ConvenienceDispatches: FC = memo(() => {
  const { startOn: startOnParam, endOn: endOnParam } = useParams<{ startOn: string; endOn: string }>();
  const navigate = useNavigate();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);

  // path param が無い / 開始日が日付として不正な場合は既定日へ、
  // レンジが不正（終了日 < 開始日・上限日数超過）な場合は終了日を開始日へ揃えてリダイレクトする。
  // V3Plans / V3Timeline と判定を揃えて、不正な URL を直接開いても壊れないようにする。
  useEffect(() => {
    if (!startOnParam || !endOnParam || new DateModel(startOnParam).isInValid()) {
      const on =
        licenseContext.config?.on ??
        localStorage.getItem('planningOn') ??
        datetimeDecorator.toYyyyMmDd(new Date());
      void navigate(`/convenience-dispatches/${on}/${on}`, { replace: true });
      return;
    }

    const { endOn: normalizedEndOn, changed } = normalizePlanningEndOn(startOnParam, endOnParam);
    if (changed) {
      void navigate(`/convenience-dispatches/${startOnParam}/${normalizedEndOn}`, { replace: true });
    }
  }, [startOnParam, endOnParam, licenseContext.config?.on, navigate]);

  const startOn = startOnParam ?? '';
  const endOn = endOnParam ?? startOn;

  // 選択中の固定ルート ID。日付ごとに localStorage に保存し、リロードや
  // ダッシュボード経由の遷移でも復元する。書き込み時に LRU で 30 エントリに整形する。
  const [fixedRouteId, setFixedRouteId] = useSelectedFixedRouteId(startOn);

  // V3Plans / V3Timeline と同様、現在の期間を plansContext / licenseContext に反映する
  useEffect(() => {
    if (!startOn || !endOn) return;
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    licenseContext.setStartEndOn({ startOn, endOn });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOn, endOn]);

  const { data: fixedRoutes = [], isLoading: isLoadingFixedRoutes } =
    useConvenienceDispatchFixedRoutesQuery();

  // 永続化されていた FR が既に削除済 / アクセス不可なら自動クリア
  useEffect(() => {
    if (fixedRouteId == null || isLoadingFixedRoutes) return;
    if (fixedRoutes.length === 0) return;
    if (!fixedRoutes.some((fr) => fr.id === fixedRouteId)) {
      setFixedRouteId(null);
    }
  }, [fixedRouteId, fixedRoutes, isLoadingFixedRoutes, setFixedRouteId]);

  // fixed route 選択時のみ detail を取得する (overlay 用補助データ)
  const fixedRouteDetailQuery = useConvenienceDispatchFixedRouteDetailQuery(fixedRouteId);

  // 表示対象（配送形態・区間種別）。取得条件なので Container で持つ（/map と同じ）
  const [orderAttrFilter, setOrderAttrFilter] = useOrderAttrFilter();
  // 取得（3 クエリ）だけデバウンスする。チェックの見た目とクライアント側の絞り込みは
  // orderAttrFilter をそのまま使うので、体感の即時性は変えない (issue #317 レビュー I-2)
  const debouncedOrderAttrFilter = useDebouncedOrderAttrFilter(orderAttrFilter);
  // 取得条件（debounce 後）と画面の状態（即時）が食い違っている間も運賃計算を止める。
  // Why not 即時値だけを見る: 解除した瞬間はガードが外れるのに、クエリはまだ絞り込み済みの
  // 結果を持っている（キーが変わるのは 400ms 後）。その窓で押すと一部の車両だけ再計算される
  // (/map と同じ理由。Codex レビュー P1-a)
  const isTariffBlockedByOrderAttrFilter =
    isOrderAttrFilterActive(orderAttrFilter)
    || orderAttrFilterToken(orderAttrFilter) !== orderAttrFilterToken(debouncedOrderAttrFilter);

  const deliveriesQuery = useConvenienceDispatchDeliveriesQuery(
    startOn,
    endOn,
    fixedRouteId,
    PER_PAGE_DELIVERIES,
    debouncedOrderAttrFilter
  );

  const legRunsQuery = useInfiniteQueryShipmentOrderLegRuns(startOn, endOn, PER_PAGE_LEG_RUNS, debouncedOrderAttrFilter);

  // 未割当一覧は積地の時刻で引くので、前日に積んで当日は降ろすだけの案件（宵積み）が
  // 入らない。deliveries の compact な legRun を補えるよう、一覧に無い案件を別に引く
  // (log_web_issues#343)。一覧が届く前に比べると全件が「無い」扱いになるので待つ
  const supplementalLegRunIds = useMemo(
    () => (legRunsQuery.isSuccess
      ? findLegRunIdsToSupplement(
        deliveriesQuery.data?.pages.flatMap((p) => p.data) ?? [],
        legRunsQuery.data.pages.flatMap((p) => p.data),
      )
      : []),
    [deliveriesQuery.data, legRunsQuery.data, legRunsQuery.isSuccess],
  );
  const supplementalLegRuns = useQuerySupplementalLegRuns(supplementalLegRunIds);

  // 本日なし判定専用: fixed route 選択時のみ対象日全件取得する (左カラム表示の pagination とは分離)
  const allDeliveriesQuery = useConvenienceDispatchAllDeliveriesQuery(
    startOn,
    endOn,
    fixedRouteId != null,
    debouncedOrderAttrFilter
  );

  // VehicleInfo 表示用のトラック/ドライバーのマスタ（V3Plans/Timeline と同じ期間指定 hook）
  const trucksQuery = useQueryPlanningsTrucks(startOn, endOn);
  const driversQuery = useQueryPlanningsDrivers(startOn, endOn);

  const applyFixedRoute = useApplyFixedRouteMutation();

  // Backdrop メッセージ切り替え用: 'call' = 呼び出し, 'align' = 対応づけ
  // 各 handler の冒頭でセットし、Backdrop 表示中の文言を切り替える。
  // Backdrop 非表示時は値が古くても影響しない (Presenter 側で applyChainBusy 判定により表示制御)。
  const [applyMode, setApplyMode] = useState<'call' | 'align'>('align');

  const handleDateRangeChange = useCallback(
    (start: string, end: string) => {
      if (start === '') return;
      // DateRangePicker は range で start / end の双方を渡す。end が未指定 (= 単日選択) なら start にフォールバックする。
      const safeEnd = end || start;
      void navigate(`/convenience-dispatches/${start}/${safeEnd}`, { replace: false });
    },
    [navigate]
  );

  // setFixedRouteId は useSelectedFixedRouteId(startOn) 由来で startOn 依存の closure。
  // 依存配列から外すと日付変更後も古い setter を閉じ込め、選択状態が古い日付の
  // localStorage キーへ書き込まれるため必ず依存に含める。
  const handleFixedRouteSelect = useCallback((id: number) => {
    setFixedRouteId(id);
  }, [setFixedRouteId]);

  const handleFixedRouteCreated = useCallback((newId: number) => {
    setFixedRouteId(newId);
  }, [setFixedRouteId]);

  const handleFixedRouteCleared = useCallback(() => {
    setFixedRouteId(null);
  }, [setFixedRouteId]);

  // 「呼び出し」系(toolbar の固定ルート選択) は apply (reset + shift 調整のみ) + refetch。
  // createOperations=false で backend にリクエストする。timeline 自動配車は走らせない。
  const handleApplyFixedRoute = useCallback((overrideId?: number) => {
    const targetId = overrideId ?? fixedRouteId;
    if (startOn === '' || targetId == null) return;

    setApplyMode('call');
    applyFixedRoute.mutate(
      { fixedRouteId: targetId, planningDate: startOn, createOperations: false },
      {
        onSuccess: () => {
          void Promise.all([
            deliveriesQuery.refetch(),
            legRunsQuery.refetch(),
            allDeliveriesQuery.refetch(),
          ]);
        },
      }
    );
  }, [startOn, fixedRouteId, applyFixedRoute, deliveriesQuery, legRunsQuery, allDeliveriesQuery]);

  // 「固定ルートに対応づけ」ボタンは apply (reset + shift 調整 + stop ↔ legRun マッチ) のみ。
  // createOperations=true で backend が leg_run + depot operation を operation_snapshot から
  // 復元するので、frontend から自動配車をチェーン発火させる必要は無い。
  const handleAlignToFixedRoute = useCallback((overrideId?: number) => {
    const targetId = overrideId ?? fixedRouteId;
    if (startOn === '' || targetId == null) return;

    setApplyMode('align');
    applyFixedRoute.mutate(
      { fixedRouteId: targetId, planningDate: startOn, createOperations: true },
      {
        onSuccess: () => {
          void Promise.all([
            deliveriesQuery.refetch(),
            legRunsQuery.refetch(),
            allDeliveriesQuery.refetch(),
          ]);
        },
      }
    );
  }, [startOn, fixedRouteId, applyFixedRoute, deliveriesQuery, legRunsQuery, allDeliveriesQuery]);

  const handleNavigate = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate]
  );

  // 未割当リストの32列フィルタ向けの選択肢・準備状態。Map/index.tsx と同じ導出。
  const trucksForFilter = useMemo(
    () => (trucksQuery.data ?? []).map((t) => ({ id: t.id, name: t.truckNumber || t.licensePlateValue })),
    [trucksQuery.data],
  );
  const driversForFilter = useMemo(
    () => (driversQuery.data ?? []).map((d) => ({ id: d.id, name: d.name })),
    [driversQuery.data],
  );
  const customInputFieldNames = useMemo(
    () => licenseContext.config?.custom_input_fields ?? [],
    [licenseContext.config],
  );

  return (
    <ConvenienceDispatchesPresenter
      startOn={startOn}
      endOn={endOn}
      fixedRouteId={fixedRouteId}
      fixedRoutes={fixedRoutes}
      isLoadingFixedRoutes={isLoadingFixedRoutes}
      isApplying={applyFixedRoute.isPending}
      deliveriesQuery={deliveriesQuery}
      legRunsQuery={legRunsQuery}
      supplementalLegRuns={supplementalLegRuns}
      fixedRouteDetailQuery={fixedRouteDetailQuery}
      allDeliveriesQuery={allDeliveriesQuery}
      orderAttrFilter={orderAttrFilter}
      onOrderAttrFilterChange={setOrderAttrFilter}
      isTariffBlockedByOrderAttrFilter={isTariffBlockedByOrderAttrFilter}
      trucks={trucksQuery.data ?? []}
      drivers={driversQuery.data ?? []}
      onDateRangeChange={handleDateRangeChange}
      onFixedRouteSelect={handleFixedRouteSelect}
      onFixedRouteCreated={handleFixedRouteCreated}
      onFixedRouteCleared={handleFixedRouteCleared}
      onApplyFixedRoute={handleApplyFixedRoute}
      onAlignToFixedRoute={handleAlignToFixedRoute}
      applyMode={applyMode}
      onNavigate={handleNavigate}
      customInputFieldNames={customInputFieldNames}
      trucksForFilter={trucksForFilter}
      driversForFilter={driversForFilter}
      companyId={String(licenseContext.config?.selected_company_id ?? 0)}
      isLicenseReady={Boolean(licenseContext.config)}
      useAlgorithmPlanning={Boolean(licenseContext.config?.use_algorithm_planning)}
      deliveryListGrouping={licenseContext.config?.delivery_list_grouping ?? 'groupByAddress'}
      selectedCompanyId={licenseContext.config ? licenseContext.config.selected_company_id : null}
      isMastersReady={trucksQuery.isSuccess && driversQuery.isSuccess}
      isMastersError={trucksQuery.isError || driversQuery.isError}
    />
  );
});

export default ConvenienceDispatches;
